import React from "react";
import { Box, Divider, Link, Tooltip, Typography } from "@mui/material";
import { ArrowForwardRounded as ArrowIcon } from "@mui/icons-material";
import { Popup } from "react-leaflet";
import type { LatLngTuple, PointExpression } from "leaflet";
import { colors, fonts } from "../../../styles/theme";

const POPUP_WIDTH = 280;

interface MapFeaturePopupProps {
  /** Stelle auf der Karte, an der das Popup steht – stabil halten, sonst öffnet es neu */
  position: LatLngTuple;
  /** Versatz der Spitze zur Stelle in px; ohne Angabe der von Leaflet */
  offset?: PointExpression;
  /** Leaflet hat das Popup geschlossen – per ×, Klick in die Karte oder ein anderes Popup */
  onClose: () => void;
  /** Art des Elements über dem Titel, etwa „Flurstück“ */
  kind: string;
  title: string;
  /** Der Titel wird zum Verweis, etwa auf die Seite mit allen Angaben */
  titleLink?: { tooltip: string; onClick: () => void };
  /** Zeile unter dem Titel – Text oder z. B. Fläche und Status-Chip */
  subtitle?: React.ReactNode;
  /** Abschnitte (`MapFeaturePopupSection`) bzw. Kennzahlen (`MapFeaturePopupFigures`) */
  children?: React.ReactNode;
}

/**
 * Popup für ein Element der Karte: oben Art und Titel (optional als Verweis), darunter
 * Abschnitte.
 *
 * Typografie wie in den Listen der Anwendung (etwa der Terminliste): Titel `body2` halbfett,
 * alles Weitere `body2` bzw. `caption` – nichts größer, keine Versalien.
 *
 * Kennt keine Fachlichkeit – was drinsteht, entscheidet der Aufrufer. Steht frei auf der Karte
 * statt an einem Layer, weil Layer wie `LandParcelLayer` ihre Formen direkt in Leaflet anlegen
 * und kein React-Kind tragen können.
 */
const MapFeaturePopup: React.FC<MapFeaturePopupProps> = ({
  position,
  offset,
  onClose,
  kind,
  title,
  titleLink,
  subtitle,
  children,
}) => (
  <Popup
    position={position}
    // Nur mitgeben, wenn gesetzt: Leaflet übernähme `undefined` statt seines Standards und
    // stürzte beim Positionieren ab
    {...(offset !== undefined && { offset })}
    minWidth={POPUP_WIDTH}
    maxWidth={POPUP_WIDTH}
    eventHandlers={{ remove: onClose }}
  >
    <Box
      sx={{
        fontFamily: fonts.primary,
        color: colors.text.primary,
        // Leaflet setzt Absätzen im Popup großzügige Ränder – die Typografie hier bringt ihre
        // eigenen Abstände mit
        "&& p": { m: 0 },
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: "block", color: colors.text.secondary }}
      >
        {kind}
      </Typography>
      {/* Rechts Platz für das × von Leaflet */}
      <Box sx={{ pr: 2 }}>
        {titleLink ? (
          <Tooltip title={titleLink.tooltip} placement="top-start">
            <Link
              component="button"
              onClick={titleLink.onClick}
              underline="none"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                textAlign: "left",
                color: colors.text.primary,
                "& .arrow": {
                  color: colors.brand.teal600,
                  transition: "transform 0.15s ease",
                },
                "&:hover": { color: colors.brand.teal600 },
                "&:hover .arrow": { transform: "translateX(2px)" },
              }}
            >
              <PopupTitle>{title}</PopupTitle>
              <ArrowIcon className="arrow" sx={{ fontSize: 15 }} />
            </Link>
          </Tooltip>
        ) : (
          <PopupTitle>{title}</PopupTitle>
        )}
      </Box>
      {subtitle && (
        <Box
          sx={{
            mt: 0.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: colors.text.secondary,
            typography: "body2",
          }}
        >
          {subtitle}
        </Box>
      )}
      {children}
    </Box>
  </Popup>
);

const PopupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="span"
    variant="body2"
    sx={{ fontWeight: fonts.weights.semiBold, lineHeight: 1.5 }}
  >
    {children}
  </Typography>
);

interface MapFeaturePopupSectionProps {
  /** Überschrift des Abschnitts */
  label: string;
  children: React.ReactNode;
}

/** Abschnitt im `MapFeaturePopup`, durch eine Linie vom vorigen getrennt */
export const MapFeaturePopupSection: React.FC<MapFeaturePopupSectionProps> = ({
  label,
  children,
}) => (
  <>
    <Divider sx={{ my: 1.25, borderColor: colors.border.divider }} />
    <Typography
      variant="caption"
      sx={{ display: "block", color: colors.text.secondary, mb: 0.5 }}
    >
      {label}
    </Typography>
    {children}
  </>
);

interface MapFeaturePopupFiguresProps {
  figures: { label: string; value: string }[];
}

/** Kennzahlen als Kacheln nebeneinander – Wert, Bezeichnung darunter; wie die `StatChip`s */
export const MapFeaturePopupFigures: React.FC<MapFeaturePopupFiguresProps> = ({
  figures,
}) => (
  <Box
    sx={{
      mt: 1.25,
      display: "grid",
      gridTemplateColumns: `repeat(${figures.length}, minmax(0, 1fr))`,
      gap: 1,
    }}
  >
    {figures.map((figure) => (
      <Box
        key={figure.label}
        sx={{
          backgroundColor: colors.brand.teal50,
          borderRadius: 1,
          px: 1.25,
          py: 0.75,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            display: "block",
            color: colors.brand.teal600,
            fontWeight: fonts.weights.semiBold,
            whiteSpace: "nowrap",
          }}
        >
          {figure.value}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
          {figure.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default MapFeaturePopup;
